"use client";

import * as React from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface FieldContextValue {
  id: string;
  hasError: boolean;
  errors: string[];
  isRequired: boolean;
}

const FieldContext = React.createContext<FieldContextValue>({
  id: "",
  hasError: false,
  errors: [],
  isRequired: false,
});

export function useFieldWrapperContext() {
  return React.useContext(FieldContext);
}

// --- Field ---

export interface FieldProps {
  children: React.ReactNode;
  className?: string;
  errors?: unknown[];
  isTouched?: boolean;
  isRequired?: boolean;
}

function normalizeErrors(errors: unknown[]): string[] {
  return errors
    .map((e) => {
      if (typeof e === "string") return e;
      if (typeof e === "object" && e && "message" in e) return (e as { message: string }).message;
      return null;
    })
    .filter((e): e is string => e !== null);
}

export function Field({ children, className, errors = [], isTouched = false, isRequired = false }: FieldProps) {
  const id = React.useId();
  const errorStrings = normalizeErrors(errors);
  const hasError = isTouched && errorStrings.length > 0;

  return (
    <FieldContext.Provider value={{ id, hasError, errors: hasError ? errorStrings : [], isRequired }}>
      <div className={cn("w-full", className)} data-invalid={hasError ? "" : undefined}>
        {children}
      </div>
    </FieldContext.Provider>
  );
}

// --- FieldLabel ---

export function FieldLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  const { id, isRequired } = useFieldWrapperContext();
  return (
    <Label htmlFor={id} className={className}>
      {children}
      {isRequired && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

// --- FieldDescription ---

export function FieldDescription({ children, className }: { children: React.ReactNode; className?: string }) {
  const { id } = useFieldWrapperContext();
  return (
    <p id={`${id}-desc`} className={cn("text-xs text-muted-foreground mt-0.5", className)}>
      {children}
    </p>
  );
}

// --- FieldError ---

export function FieldError({ className }: { className?: string }) {
  const { hasError, errors, id } = useFieldWrapperContext();
  if (!hasError) return null;
  return (
    <p id={`${id}-error`} role="alert" className={cn("text-xs text-destructive mt-1", className)}>
      {errors[0]}
    </p>
  );
}
